import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { initialState } from "./register.initial.state";
import {
	CompanyLookupMappedData,
	CompanySearchData,
	registrationInitialState,
} from "./register.types";

import {
	checkIfUsernameExists,
	getProfileData,
	registerBusiness,
	registerUser,
	searchCompanyData,
	searchCompanyDetails,
	searchCompanySubUnits,
	validatePostcodeLookup,
	verifyProfile,
} from "./register.actions";

import { normalizeValue } from "../../../utils/helpers";
import { AlertI } from "../../settings/common.types";

const initState: registrationInitialState = initialState;

export const registerSlice = createSlice({
	name: "registration",
	initialState: initState,
	reducers: {
		showConditionsModal: (state) => {
			state.showConditionsModal = true;
		},
		showNewsletterModal: (state) => {
			state.showNewsletterModal = true;
		},
		hideModal: (state) => {
			state.showConditionsModal = false;
			state.showNewsletterModal = false;
		},
		triggerAlert: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts.push(...action.payload);
		},
		onAlertClose: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts = action.payload;
		},
		handleFieldValueChange: (
			state,
			action: PayloadAction<{ form: string; field: string; value: any }>,
		) => {
			state[action.payload.form].form[action.payload.field] = normalizeValue(
				action.payload.value,
			);
		},
		resetLookupData: (state) => {
			state.b2b.organisationNumberValidated = false;
			state.b2b.subunitOrganisationNumberValidated = false;
			state.b2b.postcodeValidated = false;
			state.b2b.businessNameValidated = false;
			state.b2b.lastCheckedPostcodeNumber = "";
			state.b2b.lastCheckedOrganisationNumber = "";
			state.b2b.isSubunitSearchActive = false;
			state.b2b.missingSubunitData = false;
			state.b2b.form.lookupId = "";
			state.b2b.form.canonicalId = "";
			state.b2b.form.companyData = [];
			state.b2b.form.subunitsCompanyData = [];
			state.b2b.form.organisationNumber = "";
			state.b2b.form.subunitOrganisationNumber = "";
			state.b2b.form.subunitName = "";
			state.b2b.form.businessName = "";
			state.b2b.form.companyAddress = "";
			state.b2b.form.postcode = "";
			state.b2b.form.city = "";
			state.b2b.form.email = "";
			state.b2b.form.name = "";
			state.b2b.form.phone = "";
			state.b2b.form.password = "";
			state.b2b.form.invoiceEmail = "";
			state.b2b.form.acceptSalesConditions = false;
			state.b2b.form.acceptSmsNotifications = false;
			state.b2b.form.acceptOffersInformations = false;
			state.b2b.form.acceptMarketingConsents = false;
			state.b2b.form.accept3rdPartyMarketingConsents = false;
		},
		resetEmailLookup: (state) => {
			state.b2b.isCheckingUsername = false;
			state.b2b.usernameExists = false;
			state.b2b.savedUsername = null;
		},
		toggleCompanyDataEditMode: (state, action: PayloadAction<boolean>) => {
			state.b2b.isEditCompanyDataMode = action.payload;
		},
		toggleSubunitSearch: (state, action: PayloadAction<boolean>) => {
			state.b2b.isSubunitSearchActive = action.payload;
		},
		validateCompany: (state, action: PayloadAction<boolean>) => {
			state.b2b.businessNameValidated = action.payload;
		},
		validateSubunit: (state, action: PayloadAction<boolean>) => {
			state.b2b.subunitOrganisationNumberValidated = action.payload;
		},
		isLookupMissingSubunitData: (state, action: PayloadAction<boolean>) => {
			state.b2b.missingSubunitData = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(registerUser.pending, (state) => {
			state.isRegisteringNewUser = true;
		});
		builder.addCase(
			registerUser.fulfilled,
			(
				state,
				action: PayloadAction<{ isSuccess?: boolean; error?: string }>,
			) => {
				state.isRegisteringNewUser = false;
				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(registerUser.rejected, (state) => {
			state.isRegisteringNewUser = false;
		});

		builder.addCase(registerBusiness.pending, (state) => {
			state.isRegisteringNewUser = true;
		});
		builder.addCase(
			registerBusiness.fulfilled,
			(
				state,
				action: PayloadAction<{ isSuccess?: boolean; error?: string }>,
			) => {
				state.isRegisteringNewUser = false;
				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(registerBusiness.rejected, (state) => {
			state.isRegisteringNewUser = false;
		});

		builder.addCase(searchCompanyData.pending, (state) => {
			state.b2b.form.isFetchingCompanyData = true;
			state.b2b.form.businessName = "";
			state.b2b.form.companyAddress = "";
			state.b2b.form.postcode = "";
			state.b2b.form.city = "";
			state.b2b.form.isPublic = false;
			state.b2b.form.companyData = [];
			state.b2b.hasNoResults = false;
		});
		builder.addCase(
			searchCompanyData.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					query: string;
					error?: string;
					result?: CompanyLookupMappedData[];
				}>,
			) => {
				state.b2b.form.isFetchingCompanyData = false;
				state.b2b.lastCheckedOrganisationNumber = action.payload.query;
				state.b2b.hasNoResults = action.payload?.result?.length === 0;

				state.b2b.form.companyData = action.payload?.result;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(searchCompanyData.rejected, (state) => {
			state.b2b.form.isFetchingCompanyData = false;
			state.b2b.organisationNumberValidated = false;
		});

		builder.addCase(searchCompanySubUnits.pending, (state) => {
			state.b2b.form.isFetchingSubunits = true;
		});
		builder.addCase(
			searchCompanySubUnits.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					canonicalId?: string;
					error?: string;
					result?: any;
				}>,
			) => {
				state.b2b.form.isFetchingSubunits = false;
				state.b2b.form.canonicalId = action.payload.canonicalId;
				state.b2b.form.subunitsCompanyData = action.payload?.result;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(searchCompanySubUnits.rejected, (state) => {
			state.b2b.form.isFetchingSubunits = false;
			state.b2b.organisationNumberValidated = false;
		});

		builder.addCase(
			searchCompanyDetails.pending,
			(state, action: { meta: { arg: { lookupId: string } } }) => {
				state.b2b.form.isFetchingCompanyData = true;
				state.b2b.form.lookupId = action.meta.arg.lookupId;
			},
		);
		builder.addCase(
			searchCompanyDetails.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					lookupId: string;
					error?: string;
					result?: CompanySearchData;
				}>,
			) => {
				state.b2b.form.isFetchingCompanyData = false;
				state.b2b.form.businessName = action.payload.result.name;
				state.b2b.form.companyAddress =
					action.payload.result.addresses?.[0]?.street_address;
				state.b2b.form.postcode =
					action.payload.result.addresses?.[0]?.postal_code;
				state.b2b.form.city = action.payload.result.addresses?.[0]?.city;
				state.b2b.form.isPublic = state.b2b.isSubunitSearchActive;
				state.b2b.businessNameValidated = !!action.payload.result.name;
				state.b2b.organisationNumberValidated = !!action.payload.result.name;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(searchCompanyDetails.rejected, (state) => {
			state.b2b.form.isFetchingCompanyData = false;
		});

		builder.addCase(
			checkIfUsernameExists.pending,
			(state, action: { meta: { arg: { form: string } } }) => {
				state[action.meta.arg.form].isCheckingUsername = true;
			},
		);
		builder.addCase(
			checkIfUsernameExists.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					form?: string;
					username?: string;
					error?: string;
					result?: { isSuccess: boolean; userExists: boolean };
				}>,
			) => {
				state[action.payload.form].isCheckingUsername = false;
				state[action.payload.form].savedUsername = action.payload.username;
				state[action.payload.form].usernameExists =
					action.payload.result.userExists;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
		builder.addCase(checkIfUsernameExists.rejected, (state) => {
			state.b2b.isFetching = false;
		});

		builder.addCase(
			validatePostcodeLookup.pending,
			(
				state,
				action: {
					meta: {
						arg: {
							form: string;
							postcode: string;
							target: string;
							countryCode: string;
						};
					};
				},
			) => {
				state[action.meta.arg.form].lastCheckedPostcodeNumber =
					action.meta.arg.postcode;
			},
		);
		builder.addCase(
			validatePostcodeLookup.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					form?: string;
					postcode?: string;
					target?: string;
					result?: string;
					error?: string;
				}>,
			) => {
				state[action.payload.form].postcodeValidated = true;
				state[action.payload.form].lastCheckedPostcodeNumber =
					`${action.payload.target}_${action.payload.postcode}`;
				state[action.payload.form].form.postcode = action.payload.postcode;
				state[action.payload.form].form.city = action.payload.result;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);

		builder.addCase(getProfileData.pending, (state) => {
			state.profile.isFetching = true;
		});
		builder.addCase(
			getProfileData.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					result?: {
						id: string;
						role: string;
						username: string;
						isClubMember: boolean;
					};
					error: string;
				}>,
			) => {
				state.profile.profileDataReady = !!(
					action.payload?.result?.username && action.payload?.result?.role
				);
				state.profile.isFetching = false;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}

				state.profile.form.id = action.payload?.result?.id;
				state.profile.form.email = action.payload?.result?.username;
				state.profile.form.role = action.payload?.result?.role;
				state.profile.form.isClubMember = action.payload?.result?.isClubMember;
			},
		);

		builder.addCase(verifyProfile.pending, (state) => {
			state.profile.isFetching = true;
		});
		builder.addCase(
			verifyProfile.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					returnUrl?: string;
					error?: string;
				}>,
			) => {
				state.profile.isFetching = false;

				if (!action.payload.isSuccess) {
					state.alerts.push({
						messageKey: action.payload.error,
						level: "error",
					});
				}
			},
		);
	},
});

export const {
	showConditionsModal,
	showNewsletterModal,
	triggerAlert,
	onAlertClose,
	hideModal,
	resetEmailLookup,
	resetLookupData,
	validateCompany,
	validateSubunit,
	handleFieldValueChange,
	toggleCompanyDataEditMode,
	toggleSubunitSearch,
	isLookupMissingSubunitData,
} = registerSlice.actions;

export default registerSlice.reducer;
